import { sanitizedAttributionSearch } from './email-attribution.js'

const EVENT_PARAMETERS = Object.freeze({
  page_view: ['page_type', 'page_location', 'page_title'],
  game_start: ['scenario_id', 'entry_point', 'start_type'],
  turn_submit: ['scenario_id', 'input_method', 'mode', 'turn_bucket'],
  ending_reached: ['scenario_id', 'ending_id', 'turn_count_bucket'],
  vg_video_start: ['video_id', 'video_type', 'placement'],
  vg_video_progress: ['video_id', 'video_type', 'placement', 'video_percent'],
  vg_video_complete: ['video_id', 'video_type', 'placement'],
  vg_video_error: ['video_id', 'placement', 'error_code'],
  ui_click: ['element_id', 'surface'],
  scene_click: ['scene_id', 'interaction_result', 'grid_x', 'grid_y', 'pointer_type'],
  billing_paypoint_gameplay: [],
  billing_paypoint_ending_video: [],
  billing_paypoint_story_video: [],
  login: ['method'],
  sign_up: ['method'],
  content_click: ['content_type', 'content_id', 'content_title', 'surface', 'position', 'destination'],
})

const INPUT_METHODS = new Set(['typed', 'voice', 'suggested_choice', 'map_move'])
const TURN_MODES = new Set(['say', 'act'])
const VIDEO_PROGRESS_MARKS = new Set([25, 50, 75])
const INTERACTION_RESULTS = new Set(['hit', 'miss'])
const AUTH_METHODS = new Set(['email', 'google', 'apple', 'x', 'discord', 'facebook', 'tiktok'])
const CONTENT_TYPES = new Set(['game', 'drama'])
const CONTENT_SURFACES = new Set(['homepage_carousel', 'homepage_game', 'homepage_drama'])

/**
 * @typedef {{ app_name: string, environment: string, app_version: string, locale: string, auth_state: string, page_type: string }} AnalyticsContext
 */

/**
 * @param {{ getContext: () => AnalyticsContext, send: (name: string, parameters: Record<string, string|number|boolean>) => void }} options
 */
export function createVisualGameAnalytics({ getContext, send }) {
  if (typeof getContext !== 'function') throw new Error('Visual Game analytics getContext is required')
  if (typeof send !== 'function') throw new Error('Visual Game analytics send is required')

  function track(name, parameters = {}) {
    const required = EVENT_PARAMETERS[name]
    if (!required) throw new Error(`Unhandled Visual Game analytics event: ${name}`)
    for (const parameter of required) requireParameter(name, parameters, parameter)
    validateEvent(name, parameters)
    const context = getContext()
    validateContext(context)
    send(name, { ...context, ...parameters })
  }

  return Object.freeze({ track })
}

/**
 * @param {string} pathname
 */
export function pageDescriptor(pathname) {
  if (pathname === '/' || pathname === '/landing') return { page_type: 'landing', page_title: 'Visual Game Landing' }
  if (pathname === '/stories') return { page_type: 'stories', page_title: 'Visual Game Stories' }
  if (pathname === '/dramas') return { page_type: 'videos', page_title: 'Visual Game Dramas' }
  if (pathname === '/search') return { page_type: 'search', page_title: 'Visual Game Search' }
  if (pathname === '/live') return { page_type: 'live', page_title: 'Live Rooms' }
  if (pathname.startsWith('/live/')) return { page_type: 'live_room', page_title: 'Live Room' }
  if (pathname === '/login') return { page_type: 'login', page_title: 'Visual Game Login' }
  if (pathname === '/game') return { page_type: 'game', page_title: 'Visual Game' }
  if (pathname === '/index' || pathname === '/play') return { page_type: 'game_legacy_entry', page_title: 'Visual Game' }
  if (pathname === '/story-tree') return { page_type: 'story_tree', page_title: 'Story Tree' }
  if (pathname.startsWith('/story/')) return { page_type: 'story_detail', page_title: 'Story Detail' }
  if (pathname === '/account') return { page_type: 'account_center', page_title: 'Account Center' }
  if (pathname === '/account/edit') return { page_type: 'account_edit', page_title: 'Edit Account' }
  if (pathname === '/account/settings') return { page_type: 'account_settings', page_title: 'Account Settings' }
  if (pathname === '/my-games') return { page_type: 'my_games', page_title: 'My Games' }
  if (pathname === '/my-videos') return { page_type: 'my_videos', page_title: 'My Videos' }
  if (pathname === '/my-videos/watch') return { page_type: 'my_video_player', page_title: 'My Video Player' }
  if (pathname === '/my-favorites') return { page_type: 'my_favorites', page_title: 'My Favorites' }
  if (pathname === '/video-generation') return { page_type: 'video_generation_manager', page_title: 'Video Generation Manager' }
  if (pathname === '/create') return { page_type: 'creator_projects', page_title: 'Creator Projects' }
  if (pathname === '/create/new') return { page_type: 'creator', page_title: 'Visual Game Creator' }
  if (pathname === '/billing') return { page_type: 'billing', page_title: 'Credits and Orders' }
  if (pathname === '/credit') return { page_type: 'mobile_credit', page_title: 'My Credits' }
  if (pathname === '/credit/history') return { page_type: 'mobile_credit_history', page_title: 'Credit History' }
  if (pathname === '/waitlist') return { page_type: 'waitlist', page_title: 'Waitlist' }
  if (pathname === '/invite' || pathname.startsWith('/invite/')) return { page_type: 'invite', page_title: 'Invite Registration' }
  if (pathname.startsWith('/players/')) return { page_type: 'player_profile', page_title: 'Player Profile' }
  if (pathname === '/run-history.html') return { page_type: 'run_history', page_title: 'Run History' }
  if (pathname === '/trpg-notes.html') return { page_type: 'notes', page_title: 'Run Notes' }
  if (pathname === '/video-center.html') return { page_type: 'video_center', page_title: 'Video Center' }
  if (pathname === '/legal/privacy') return { page_type: 'privacy', page_title: 'Privacy Policy' }
  if (pathname === '/legal/terms') return { page_type: 'terms', page_title: 'Terms of Service' }
  if (pathname === '/contact') return { page_type: 'contact', page_title: 'Contact' }
  throw new Error(`Unhandled Visual Game analytics pathname: ${pathname}`)
}

/**
 * @param {{ origin: string, pathname: string, search?: string }} locationLike
 */
export function sanitizedPageLocation(locationLike) {
  return `${locationLike.origin}${normalizedPathname(locationLike.pathname)}${sanitizedAttributionSearch(locationLike.search || '')}`
}

/**
 * @param {number} turn
 */
export function turnBucket(turn) {
  if (!Number.isInteger(turn) || turn < 1) throw new Error(`Invalid analytics turn: ${turn}`)
  if (turn <= 5) return '1_5'
  if (turn <= 10) return '6_10'
  if (turn <= 20) return '11_20'
  return '21_plus'
}

function normalizedPathname(pathname) {
  if (pathname.startsWith('/players/')) return '/players/:player'
  if (pathname.startsWith('/story/')) return '/story/:story'
  if (pathname.startsWith('/live/')) return '/live/:room'
  return pathname
}

function validateContext(context) {
  for (const parameter of ['app_name', 'environment', 'app_version', 'locale', 'auth_state', 'page_type']) {
    requireParameter('analytics_context', context, parameter)
  }
}

function validateEvent(name, parameters) {
  if (name === 'turn_submit') {
    if (!INPUT_METHODS.has(parameters.input_method)) throw new Error(`Invalid turn_submit input_method: ${parameters.input_method}`)
    if (!TURN_MODES.has(parameters.mode)) throw new Error(`Invalid turn_submit mode: ${parameters.mode}`)
  }
  if (name === 'vg_video_progress' && !VIDEO_PROGRESS_MARKS.has(parameters.video_percent)) {
    throw new Error(`Invalid vg_video_progress video_percent: ${parameters.video_percent}`)
  }
  if (name === 'scene_click' && !INTERACTION_RESULTS.has(parameters.interaction_result)) {
    throw new Error(`Invalid scene_click interaction_result: ${parameters.interaction_result}`)
  }
  if ((name === 'login' || name === 'sign_up') && !AUTH_METHODS.has(parameters.method)) {
    throw new Error(`Invalid ${name} method: ${parameters.method}`)
  }
  if (name === 'content_click') {
    if (!CONTENT_TYPES.has(parameters.content_type)) throw new Error(`Invalid content_click content_type: ${parameters.content_type}`)
    if (!CONTENT_SURFACES.has(parameters.surface)) throw new Error(`Invalid content_click surface: ${parameters.surface}`)
    if (!Number.isInteger(parameters.position) || parameters.position < 1) {
      throw new Error(`Invalid content_click position: ${parameters.position}`)
    }
  }
  for (const [key, value] of Object.entries(parameters)) {
    if (!['string', 'number', 'boolean'].includes(typeof value)) {
      throw new Error(`Invalid ${name} parameter ${key}: ${String(value)}`)
    }
  }
}

function requireParameter(eventName, parameters, parameter) {
  const value = parameters[parameter]
  if (value === undefined || value === null || value === '') {
    throw new Error(`Missing ${eventName} analytics parameter: ${parameter}`)
  }
}
